import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { AppResource } from 'src/app/app.resource';
import { SurfaceDependance, TypeHabitation } from 'src/app/core/models/form-state.model';
import { Question } from 'src/app/core/models/question.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { State } from 'src/app/core/state/core.state';
import * as fromPath from 'src/app/core/state/path';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { PopUpInfoVM } from 'src/app/shared/models/components/popupinfoVm';
import * as fromHousing from '../../../core/state/housing';
import { HousingState } from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';
import { DependencyEnum } from './../../../shared/models/enum/dependency.enum';

import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { Stepper } from 'src/app/core/models/stepper.model';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import {
  INSURED_VALUE_PATH,
  PARTICULARITY_PATH,
  ROOT_PATH
} from 'src/app/shared/constants/route.constants';
import { QuestionVM } from 'src/app/shared/models/components/questionVm';

export interface OptionsDependency {
  key: DependencyEnum;
  label: string;
  value: SurfaceDependance;
  step: string;
}

@Component({
  selector: 'app-dependency',
  templateUrl: './dependency.component.html',
  styleUrls: ['./dependency.component.scss']
})
export class DependencyComponent extends BaseComponent implements OnInit {
  popUpInfo!: PopUpInfoVM;
  dependencyEnum = DependencyEnum;

  housingData!: HousingState;
  displayModal: boolean = false;

  options: FormArray<
    FormGroup<{
      key: FormControl<DependencyEnum>;
      selected: FormControl<boolean>;
      label: FormControl<string>;
      value: FormControl<SurfaceDependance>;
      step: FormControl<string>;
    }>
  > = new FormArray<
    FormGroup<{
      key: FormControl<DependencyEnum>;
      selected: FormControl<boolean>;
      label: FormControl<string>;
      value: FormControl<SurfaceDependance>;
      step: FormControl<string>;
    }>
  >([]);

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepDependency,
    subStepNumber: 8
  };

  override question: Question = {
    libelle: this.resource.question.dependency,
    popupInfo: undefined
    // popupInfo: new PopUpInfoVM(this.resource.infobulle.dependency, '', '', '', '')
  };

  public override currentPage = MatomoConstants.DEPENDANCES;

  path: string = '';
  pathType = PathType;

  constructor(store: Store<State>, resources: AppResource, private readonly router: Router) {
    super(store, resources);
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.loadOptions();
    this.updateResumeStepVisibility(true);
    this.loadStateData();
  }

  private loadOptions() {
    const array = [
      {
        key: DependencyEnum.NoDependency,
        label: this.resource.dependency.noDependency,
        value: SurfaceDependance.Aucune,
        step: this.resource.resumestep.noDependency
      },
      {
        key: DependencyEnum.Inferior50,
        label: this.resource.dependency.inferior50,
        value: SurfaceDependance.MoinsDe50m2,
        step: this.resource.resumestep.lessThan50m
      },
      {
        key: DependencyEnum.Inferior99,
        label: this.resource.dependency.inferior99,
        value: SurfaceDependance.De50a100m2,
        step: this.resource.resumestep.lessThan100m
      },
      {
        key: DependencyEnum.Superior100,
        label: this.resource.dependency.superior100,
        value: SurfaceDependance.PlusDe100m2,
        step: this.resource.resumestep.moreThan100m
      },
      {
        key: DependencyEnum.Superior250,
        label: this.resource.dependency.superior250,
        value: SurfaceDependance.PlusDe250m2,
        step: this.resource.resumestep.moreThan100m
      },
      {
        key: DependencyEnum.Inferior40,
        label: this.resource.dependency.inferior40,
        value: SurfaceDependance.MoinsDe40m2,
        step: this.resource.resumestep.lessThan40m
      },
      {
        key: DependencyEnum.Superior40,
        label: this.resource.dependency.superior40,
        value: SurfaceDependance.PlusDe40m2,
        step: this.resource.resumestep.moreThan40m
      }
    ];
    array.forEach((item: OptionsDependency) => {
      const formGroup = new FormGroup({
        key: new FormControl<DependencyEnum>(item.key, { nonNullable: true }),
        selected: new FormControl<boolean>(false, { nonNullable: true }),
        label: new FormControl<string>(item.label, { nonNullable: true }),
        value: new FormControl<SurfaceDependance>(item.value, { nonNullable: true }),
        step: new FormControl<string>(item.step, { nonNullable: true })
      });
      this.options.push(formGroup);
    });
  }

  private async loadStateData() {
    // Assign by default
    this.popUpInfo = new PopUpInfoVM([this.resource.infobulle.dependency]);

    this.housingData = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));

    if (this.path != PathType.HOUSING) {
      this.question = new QuestionVM(this.resource.question.dependency1);
      this.store.dispatch(fromInfo.updateQuestion({ payload: this.question }));

      this.popUpInfo = new PopUpInfoVM([this.resource.infobulle.dependency1]);
      this.options.controls = this.options.controls.filter(
        i =>
          i.controls.key.value === DependencyEnum.NoDependency ||
          i.controls.key.value === DependencyEnum.Inferior40 ||
          i.controls.key.value === DependencyEnum.Superior40
      );
      let noDependency = this.options.controls.find(
        i => i.controls.key.value === DependencyEnum.NoDependency
      )!;

      noDependency.controls.label.setValue(this.resource.dependency.noDependency1);

      switch (this.housingData.SaisieHabitation.SurfaceDependance) {
        case SurfaceDependance.Aucune:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.NoDependency)!
            .controls.selected.setValue(true);
          break;
        case SurfaceDependance.MoinsDe40m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Inferior40)!
            .controls.selected.setValue(true);
          break;
        case SurfaceDependance.PlusDe40m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Superior40)!
            .controls.selected.setValue(true);
          break;
      }
    } else {
      this.options.controls = this.options.controls.filter(
        i =>
          i.controls.key.value !== DependencyEnum.Inferior40 &&
          i.controls.key.value !== DependencyEnum.Superior40
      );

      switch (this.housingData.SaisieHabitation.SurfaceDependance) {
        case SurfaceDependance.Aucune:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.NoDependency)!
            .controls.selected.setValue(true);
          break;
        case SurfaceDependance.MoinsDe50m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Inferior50)!
            .controls.selected.setValue(true);
          break;
        case SurfaceDependance.De50a100m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Inferior99)!
            ?.controls.selected.setValue(true);
          break;
        case SurfaceDependance.PlusDe100m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Superior100)!
            .controls.selected.setValue(true);
          break;
        case SurfaceDependance.PlusDe250m2:
          this.options.controls
            .find(i => i.controls.key.value === DependencyEnum.Superior250)!
            .controls.selected.setValue(true);
          break;
      }
    }
  }

  async handleClickOnItem(value: DependencyEnum) {
    // we check only the selected one, and set the other to false by precaution
    switch (value) {
      case DependencyEnum.NoDependency:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.NoDependency)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      case DependencyEnum.Inferior50:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Inferior50)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      case DependencyEnum.Inferior99:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Inferior99)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      case DependencyEnum.Superior100:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Superior100)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      case DependencyEnum.Superior250:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Superior250)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      // GRENOBLE/CRETEIL/MARSEILLE
      case DependencyEnum.Inferior40:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Inferior40)
          .forEach(x => x.controls.selected.setValue(false));
        break;
      case DependencyEnum.Superior40:
        this.options.controls
          .filter(i => i.controls.key.value !== DependencyEnum.Superior40)
          .forEach(x => x.controls.selected.setValue(false));
        break;
    }

    // we set the selected one to the store
    const selectedOption = this.options.controls.find(i => i.controls.key.value === value)!;
    selectedOption.controls.selected.setValue(true);

    await this.updateResumeStep(selectedOption.controls.step.value);
    this.store.dispatch(
      fromHousing.patchNumberOfPeople({
        payload: { SurfaceDependance: selectedOption.controls.value.value }
      })
    );

    //display modal if the dependency makes over 250
    if (
      selectedOption.controls.key.value === DependencyEnum.Superior250 ||
      (this.path !== PathType.HOUSING &&
        selectedOption.controls.key.value === DependencyEnum.Superior40)
    )
      this.displayModal = true;
    // otherwise we redirect to the next step
    else this.redirectRules();
  }

  cancel() {
    this.displayModal = false;
  }

  redirectRules() {
    if (
      this.housingData.SaisieHabitation.TypeHabitation == TypeHabitation.Maison &&
      this.path === PathType.HOUSING
    ) {
      this.updateStepper(true);
      this.router.navigate([`${ROOT_PATH}${PARTICULARITY_PATH}`]);
    } else {
      this.updateStepper(false);
      this.router.navigate([`${ROOT_PATH}${INSURED_VALUE_PATH}`]);
    }
  }

  async updateStepper(particularity: boolean) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.dependency
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN;
    if (particularity)
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.particularity
      );
    else
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.insuredvalue
      );

    if (subtitleN) {
      subtitleN.isActive = true;
      subtitleN.isVisible = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep(libelle: string) {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );
    const tmpResumeStep = cloneResumeStep(resumeStep);

    const stepResumeStep = tmpResumeStep.steps?.find(
      x => x.id == this.resource.resumestep.dependency
    );

    if (stepResumeStep) {
      stepResumeStep.step = libelle;
      stepResumeStep.isVisible = true;
    } else {
      tmpResumeStep.steps?.push({
        id: this.resource.resumestep.dependency,
        step: libelle,
        url: this.resource.resumestep.dependency,
        isVisible: true
      });
    }
    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }
}
